import { useState } from 'react';
import type { PendingAction } from '@/types';
import { NALLASMP_PANEL_URL, SSO_REDEEM_PATH } from '@/config';
import { requestHandoff } from '@/api';
import type { HandoffResult } from '@/api';
import { ExternalLink } from 'lucide-react';

interface Props {
  /** Live EC2 state from GET /api/status. */
  state: string;
  /** Whether a start/stop action is in flight (the dashboard polls until done). */
  pending: PendingAction;
  /** Override of the configured panel URL; defaults to NALLASMP_PANEL_URL. */
  panelUrl?: string;
  /** Per-session CSRF token (from the Worker session) — required for SSO handoff. */
  csrfToken?: string;
}

/** Open a URL in a new tab (fallback for when SSO is unavailable). */
function openInNewTab(url: string) {
  window.open(url, '_blank');
}

/**
 * POST the SSO handoff token to the panel's /api/sso/redeem endpoint in a new
 * tab. Uses a hidden form with target="_blank" so the browser performs a
 * full-page POST (the token is in the body, never in a URL). The panel
 * validates the token, mints a session cookie, and redirects to /dashboard.
 *
 * Only `noopener` is set on rel (not `noreferrer`): programmatic form
 * submissions to a new browsing context can produce Origin: null, so the
 * Referer header must be preserved as a fallback for the panel's CSRF check.
 * `noopener` still prevents the new tab from accessing window.opener.
 */
function submitSsoForm(token: string, panelUrl: string) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = panelUrl + SSO_REDEEM_PATH;
  form.target = '_blank';
  form.rel = 'noopener';
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'token';
  input.value = token;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}

/**
 * "Go to NallaSMP Panel" button.
 *
 * When the EC2 instance is `running` and no action is in flight:
 *   1. Requests an SSO handoff token from the Worker (POST /api/sso/handoff).
 *   2. If SSO is configured → POSTs the token to the panel's /api/sso/redeem
 *      in a new tab. The panel mints a session and redirects to /dashboard —
 *      the user lands on the authenticated dashboard directly.
 *   3. If SSO is NOT configured (or the request fails) → opens the canonical
 *      panel URL in a new tab (preserving existing fallback behavior). The
 *      panel's normal login page is shown.
 *
 * Only offers navigation when the instance is genuinely `running`. All other
 * states (stopped, pending transition, unknown, error) render the button hidden
 * or disabled. The button never calls /api/start or /api/stop.
 */
export default function PanelLinkButton({ state, pending, panelUrl, csrfToken }: Props) {
  const [loading, setLoading] = useState(false);
  const href = (panelUrl ?? NALLASMP_PANEL_URL).replace(/\/+$/, '');

  // A start/stop action is in flight: suppress the button entirely.
  if (pending !== null) return null;

  const handleClick = async () => {
    if (!csrfToken) {
      // No CSRF token → user may not be fully authenticated → fall back.
      openInNewTab(href);
      return;
    }

    setLoading(true);
    try {
      const { ok, data } = await requestHandoff(csrfToken);
      if (ok && data.ssoEnabled && data.token) {
        // SSO is configured: POST the token to the panel in a new tab.
        submitSsoForm(data.token, href);
      } else {
        // SSO disabled or failed: open the canonical panel URL (fallback).
        openInNewTab(href);
      }
    } catch {
      // Network error or unexpected response: fall back to the panel URL.
      openInNewTab(href);
    } finally {
      setLoading(false);
    }
  };

  if (state === 'running') {
    return (
      <div className="mt-6 rounded-xl bg-white p-6 shadow">
        <button
          onClick={handleClick}
          disabled={loading}
          className="btn w-full justify-center gap-2 bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
          aria-label="Open the NallaSMP panel in a new tab"
        >
          {loading ? (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <ExternalLink className="h-4 w-4" />
          )}
          Go to NallaSMP Panel
        </button>
      </div>
    );
  }

  // Stopped: keep the button visible but disabled, with an explanation.
  if (state === 'stopped') {
    return (
      <div className="mt-6 rounded-xl bg-white p-6 shadow">
        <button className="btn w-full justify-center gap-2" disabled>
          <ExternalLink className="h-4 w-4" />
          Go to NallaSMP Panel
        </button>
        <p className="mt-3 text-sm text-slate-500">
          The server must be running before you can open the NallaSMP panel.
        </p>
      </div>
    );
  }

  // Loading / unknown / transitional / errored states: never surface a
  // running-state cue. The Dashboard's ErrorBanner already reports errors.
  return null;
}
